import { cn } from '@/lib/utils';
import { initials } from '@/lib/initials';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Spinner } from '@/components/ui/spinner';

/**
 * A person's photo, else their initials, everywhere in jflo and the Chrome extension (which borrows
 * this file). A store gets StoreAvatar: initials on a rounded square.
 */
export type AvatarSize = '2xs' | 'xs' | 'sm' | 'default' | 'lg' | 'xl' | '2xl';

/** 16, 20, 24, 32, 40, 48 and 96px, each with initials sized to fit. */
const SIZE_CLASSES: Record<AvatarSize, { box: string; text: string }> = {
  '2xs': { box: 'size-4', text: 'text-3xs' },
  xs: { box: 'size-5', text: 'text-3xs' },
  sm: { box: 'size-6', text: 'text-2xs' },
  default: { box: 'size-8', text: 'text-xs' },
  lg: { box: 'size-10', text: 'text-sm' },
  xl: { box: 'size-12', text: 'text-base' },
  '2xl': { box: 'size-24', text: 'text-2xl' },
};

export function MemberAvatar({
  name,
  photourl,
  size = 'default',
  ring = false,
  hint,
  busy,
  className,
}: {
  /** Their name (or email); null when the person isn't known, which shows "?". */
  name: string | null | undefined;
  photourl?: string | null;
  size?: AvatarSize;
  /** A ring in the background colour, for avatars that overlap in a stack. */
  ring?: boolean;
  /** Hover text; defaults to the name. `false` when the name is beside it or a wrapper has a fuller hint. */
  hint?: string | false;
  /** A spinner over it while a new photo uploads. */
  busy?: boolean;
  /** Placing only (margins, z-index, shrink); the look comes from the props. */
  className?: string;
}) {
  const sized = SIZE_CLASSES[size];
  return (
    <Avatar
      className={cn(sized.box, ring && 'ring-2 ring-background', className)}
      data-hint={hint === false ? undefined : (hint ?? name ?? 'Someone')}
    >
      {photourl ? <AvatarImage src={photourl} alt="" referrerPolicy="no-referrer" /> : null}
      <AvatarFallback className={cn('leading-none', sized.text)}>{initials(name)}</AvatarFallback>
      {busy ? (
        <span className="absolute inset-0 flex items-center justify-center bg-background/70">
          <Spinner />
        </span>
      ) : null}
    </Avatar>
  );
}

/** A store's (team's) initials on a rounded square: the store menu and the team picker. */
export function StoreAvatar({ name, size = 'default', className }: { name: string; size?: AvatarSize; className?: string }) {
  const sized = SIZE_CLASSES[size];
  return (
    <Avatar className={cn(sized.box, 'rounded-lg', className)}>
      <AvatarFallback className={cn('rounded-lg leading-none', sized.text)}>{initials(name)}</AvatarFallback>
    </Avatar>
  );
}
